import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';

/**
 * ItemModal — overlay that shows full item details:
 * large image, name, description, ingredients list, price, and add-to-order button.
 * Slides up from bottom (mobile-friendly).
 */
export default function ItemModal({ item, restaurant, onClose }) {
  const { loc, t, addToCart, cartNotification } = useApp();
  const [closing, setClosing] = useState(false);
  const [imageError, setImageError] = useState(false);

  const symbol = restaurant?.currencySymbol || 'DH';
  const isNotifying = cartNotification === item.id;

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 250);
  };

  const handleAdd = () => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
    });
  };

  const ingredients = loc(item.ingredients) || [];

  return (
    <div
      style={{
        ...styles.overlay,
        animation: closing ? 'fadeIn 0.25s ease reverse forwards' : 'fadeIn 0.25s ease',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          ...styles.sheet,
          animation: closing ? 'slideUp 0.25s ease reverse forwards' : 'slideUp 0.3s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={styles.handleBar}>
          <div style={styles.handle} />
        </div>

        {/* Close button */}
        <button style={styles.closeBtn} onClick={handleClose} aria-label="Close">
          ✕
        </button>

        {/* Image */}
        <div style={styles.imageWrap}>
          {!imageError ? (
            <img
              src={item.image}
              alt={loc(item.name)}
              style={styles.image}
              onError={() => setImageError(true)}
            />
          ) : (
            <div style={styles.imagePlaceholder}>🍽️</div>
          )}
          {item.popular && (
            <span style={styles.popularBadge}>⭐ {t('popular')}</span>
          )}
        </div>

        {/* Content */}
        <div style={styles.content}>
          <div style={styles.nameRow}>
            <h2 style={styles.name}>{loc(item.name)}</h2>
            <span style={styles.price}>
              {item.price} <span style={styles.currency}>{symbol}</span>
            </span>
          </div>

          <p style={styles.description}>{loc(item.description)}</p>

          {/* Ingredients */}
          {Array.isArray(ingredients) && ingredients.length > 0 && (
            <div style={styles.ingredientsSection}>
              <h4 style={styles.ingredientsLabel}>{t('ingredients')}</h4>
              <div style={styles.ingredientsTags}>
                {ingredients.map((ing, i) => (
                  <span key={i} style={styles.ingredientTag}>
                    {ing}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add to order button */}
          <button
            style={{
              ...styles.addBtn,
              ...(isNotifying ? styles.addBtnSuccess : {}),
            }}
            onClick={handleAdd}
          >
            {isNotifying ? `✓ ${t('itemAdded')}` : `${t('addToOrder')} — ${item.price} ${symbol}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    backgroundColor: 'var(--bg-overlay)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    backgroundColor: 'var(--bg-primary)',
    borderTopLeftRadius: 'var(--radius-xl)',
    borderTopRightRadius: 'var(--radius-xl)',
    overflow: 'hidden',
    overflowY: 'auto',
    position: 'relative',
  },
  handleBar: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0.75rem 0 0.25rem',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 5,
  },
  handle: {
    width: '36px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  closeBtn: {
    position: 'absolute',
    top: '0.75rem',
    right: '0.75rem',
    zIndex: 10,
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'rgba(0,0,0,0.4)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
    backdropFilter: 'blur(8px)',
  },
  imageWrap: {
    width: '100%',
    height: '240px',
    backgroundColor: 'var(--bg-tertiary)',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '4rem',
    backgroundColor: 'var(--bg-tertiary)',
  },
  popularBadge: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    padding: '4px 10px',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    borderRadius: 'var(--radius-full)',
  },
  content: {
    padding: '1.25rem 1.25rem 2rem',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '0.5rem',
  },
  name: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    flex: 1,
  },
  price: {
    fontWeight: 700,
    fontSize: '1.3rem',
    color: 'var(--accent)',
    whiteSpace: 'nowrap',
  },
  currency: {
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  description: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '1.25rem',
  },
  ingredientsSection: {
    marginBottom: '1.5rem',
  },
  ingredientsLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.6rem',
  },
  ingredientsTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  ingredientTag: {
    padding: '0.3rem 0.7rem',
    fontSize: '0.78rem',
    backgroundColor: 'var(--accent-light)',
    color: 'var(--accent)',
    borderRadius: 'var(--radius-full)',
    fontWeight: 500,
    border: '1px solid var(--accent-glow)',
  },
  addBtn: {
    width: '100%',
    padding: '1rem',
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    fontWeight: 600,
    fontSize: '1rem',
    borderRadius: 'var(--radius-lg)',
    transition: 'all var(--transition)',
    boxShadow: '0 4px 20px var(--accent-glow)',
  },
  addBtnSuccess: {
    backgroundColor: 'var(--success)',
  },
};
